import { Button } from "@heroui/react";
import { useTranslation } from "react-i18next";
import type {
  EligibleShipment,
  PickupIneligibleShipment,
} from "@/api/use-schedule-pickup";
import { SelectableCard } from "./sheet-primitives";

export function PickupEligibilityNotice({
  shipments,
  onRemove,
  disabled = false,
}: {
  shipments: PickupIneligibleShipment[];
  onRemove: () => void;
  disabled?: boolean;
}) {
  const { t } = useTranslation("app");
  if (!shipments.length) return null;
  return (
    <div
      role="alert"
      className="m-4 rounded-medium border border-warning-200 bg-warning-50 p-3 text-sm"
    >
      <p className="font-semibold">{t("pickup.what.selectionChanged")}</p>
      <ul className="my-2 space-y-1">
        {shipments.map((shipment) => (
          <li key={shipment.id}>
            {shipment.shipmentNumber === undefined
              ? t("pickup.what.selectedShipment")
              : `SHP-${shipment.shipmentNumber}`}
            : {t(`pickup.what.unavailableReasons.${shipment.reason}`)}
          </li>
        ))}
      </ul>
      <Button
        size="sm"
        variant="bordered"
        onPress={onRemove}
        isDisabled={disabled}
      >
        {t("pickup.what.removeAndContinue")}
      </Button>
    </div>
  );
}

export function PickupUnavailableShipments({
  shipments,
  selectedIds,
}: {
  shipments: EligibleShipment[];
  selectedIds: Set<string>;
}) {
  const { t } = useTranslation("app");
  if (!shipments.length) return null;
  return (
    <details className="mt-4" open={shipments.length <= 5}>
      <summary className="text-sm font-semibold cursor-pointer">
        {t("pickup.what.unavailableTitle", { count: shipments.length })}
      </summary>
      <div className="flex flex-col gap-2 mt-2">
        {shipments.map((s) => (
          <SelectableCard
            key={s.id}
            disabled
            selected={selectedIds.has(s.id)}
            className="!opacity-100"
          >
            <div>
              <span className="font-mono text-sm font-semibold">
                SHP-{s.shipmentNumber}
              </span>
              <p className="text-sm text-default-700">
                {t(
                  `pickup.what.unavailableReasons.${s.pickupEligibility?.reason ?? "TASK_UNAVAILABLE"}`
                )}
              </p>
              {s.pickupEligibility?.reason === "ALREADY_RECEIVED" && (
                <p className="text-xs text-default-500">
                  {t("pickup.what.noPickupNeeded")}
                </p>
              )}
            </div>
          </SelectableCard>
        ))}
      </div>
    </details>
  );
}
